// A tiny router: the URL is the state (path + search params), navigation is instant.
import { type AnchorHTMLAttributes, type MouseEvent, useMemo, useSyncExternalStore } from "react";

const listeners = new Set<() => void>();
const emit = () => {
  for (const fn of listeners) fn();
};
window.addEventListener("popstate", emit);
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** `state` rides on the history entry (the side pane notes what it showed before, ui/SidePane.tsx). */
export function navigate(to: string, { replace = false, state = null }: { replace?: boolean; state?: unknown } = {}) {
  if (to === location.pathname + location.search) return;
  history[replace ? "replaceState" : "pushState"](state, "", to);
  emit();
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, () => location.pathname);
}

export function useSearch(): URLSearchParams {
  const search = useSyncExternalStore(subscribe, () => location.search);
  return useMemo(() => new URLSearchParams(search), [search]);
}

/** Sets (or clears, with null) search params on the current page. */
export function setSearch(params: Record<string, string | null>, { replace = true } = {}) {
  const next = new URLSearchParams(location.search);
  for (const [k, v] of Object.entries(params)) v == null || v === "" ? next.delete(k) : next.set(k, v);
  const qs = next.toString();
  navigate(location.pathname + (qs ? `?${qs}` : ""), { replace });
}

export function Link({ to, onClick, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...props} />;
}
